import {
  Alert,
  Box,
  Card,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeftLong } from 'react-icons/fa6';
import Swal from 'sweetalert2';
import { LoadingButton } from '@mui/lab';
import { createContractor, getAllContractTemplates } from '../../helper/backend_helper';
import appConfig from '../../config';
import InfoTooltip from '../InfoTooltip';

const emptyTerms = { percentage: '', contractDuration: '', terminationNotice: '', contractTemplate: '' };

const buildContractUrl = (id) => `${appConfig.CONTACT_URL}/contract/${id}`;

const AddContractor = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [standard, setStandard] = useState(emptyTerms);
  const [allInclusive, setAllInclusive] = useState(emptyTerms);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);

  async function fetchData() {
    try {
      const response = await getAllContractTemplates();
      const fetchedTemplates = response?.data?.data || [];
      setTemplates(fetchedTemplates);
      const standardDefault = fetchedTemplates.find((template) => (template.mode || 'standard') === 'standard' && template.isDefault);
      const allInclusiveDefault = fetchedTemplates.find((template) => template.mode === 'allInclusive' && template.isDefault);
      // Defaults reduce manual work when creating owner links, but fields remain editable per contract.
      if (standardDefault) setStandard((prev) => ({ ...prev, contractTemplate: standardDefault._id }));
      if (allInclusiveDefault) setAllInclusive((prev) => ({ ...prev, contractTemplate: allInclusiveDefault._id }));
    } catch (error) {
      console.error('Error fetching contract templates:', error);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  const standardTemplates = useMemo(() => templates.filter((template) => (template.mode || 'standard') === 'standard'), [templates]);
  const allInclusiveTemplates = useMemo(
    () => templates.filter((template) => template.mode === 'allInclusive'),
    [templates]
  );

  const updateTerms = (setter) => (field, value) => setter((prev) => ({ ...prev, [field]: value }));
  const setStandardField = updateTerms(setStandard);
  const setAllInclusiveField = updateTerms(setAllInclusive);

  const validate = () => {
    if (!name) return 'Name is required';
    if (!standard.contractTemplate) return 'Standard contract template is required';
    if (!standard.contractDuration) return 'Standard contract duration is required';
    if (standard.percentage === '') return 'Standard percentage is required';
    if (!standard.terminationNotice) return 'Standard cancellation notice is required';
    if (allInclusive.contractTemplate) {
      if (!allInclusive.contractDuration) return 'All Inclusive contract duration is required when its template is selected';
      if (allInclusive.percentage === '') return 'All Inclusive percentage is required when its template is selected';
      if (!allInclusive.terminationNotice) return 'All Inclusive cancellation notice is required when its template is selected';
    }
    return null;
  };

  const handleSave = async () => {
    try {
      const error = validate();
      if (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: error });
        return;
      }
      setLoading(true);
      const payload = {
        name,
        contractTemplate: standard.contractTemplate,
        contractDuration: standard.contractDuration,
        percentage: Number(standard.percentage),
        terminationNotice: standard.terminationNotice,
        standardContractTemplate: standard.contractTemplate,
        standardContractDuration: standard.contractDuration,
        standardPercentage: Number(standard.percentage),
        standardTerminationNotice: standard.terminationNotice,
        allInclusiveContractTemplate: allInclusive.contractTemplate || null,
        allInclusiveContractDuration: allInclusive.contractTemplate ? allInclusive.contractDuration : '',
        allInclusivePercentage: allInclusive.contractTemplate ? Number(allInclusive.percentage) : undefined,
        allInclusiveTerminationNotice: allInclusive.contractTemplate ? allInclusive.terminationNotice : '',
      };
      const response = await createContractor(payload, { headers: { 'Content-Type': 'application/json' } });
      const createdContract = response?.data?.data;
      const contractUrl = createdContract?.contractUrl || buildContractUrl(createdContract?._id);
      await Swal.fire({
        icon: 'success',
        title: 'Contract created successfully',
        html: `<p style="margin-bottom:10px">Owner URL:</p><input readonly style="width:100%;padding:10px;border:1px solid #ddd;border-radius:6px" value="${contractUrl}" />`,
        confirmButtonText: 'Copy URL',
      });
      if (contractUrl) navigator.clipboard?.writeText(contractUrl);
      setName('');
      setStandard(emptyTerms);
      setAllInclusive(emptyTerms);
    } catch (error) {
      console.error('Error:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: error?.response?.data?.errors?.[0]?.message || 'Failed to create contractor' });
    } finally {
      setLoading(false);
    }
  };

  const renderModeColumn = (title, description, values, setField, templateOptions, required) => (
    <Card variant="outlined" sx={{ p: 2, width: { xs: '100%', md: '48%' } }}>
      <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{description}</Typography>
      <Stack spacing={3}>
        <FormControl fullWidth required={required}>
          <FormLabel sx={{ mb: 1 }}>{title} Template{required ? ' *' : ''}</FormLabel>
          <Select value={values.contractTemplate} onChange={(e) => setField('contractTemplate', e.target.value)} displayEmpty>
            <MenuItem value=""><em>{required ? 'Select template' : 'Not offered to owner'}</em></MenuItem>
            {templateOptions.map((template) => <MenuItem key={template._id} value={template._id}>{template.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField fullWidth type="number" label="Percentage" value={values.percentage} onChange={(e) => setField('percentage', e.target.value)} required={required || !!values.contractTemplate} />
          <TextField fullWidth label="Duration" value={values.contractDuration} onChange={(e) => setField('contractDuration', e.target.value)} required={required || !!values.contractTemplate} />
        </Stack>
        <TextField fullWidth label="Cancellation Notice" value={values.terminationNotice} onChange={(e) => setField('terminationNotice', e.target.value)} required={required || !!values.contractTemplate} />
      </Stack>
    </Card>
  );

  return (
    <>
      <Helmet><title>Add Contractor</title></Helmet>
      <Card sx={{ padding: 3 }}>
        <Stack onClick={() => navigate(-1)} gap={3} sx={{ cursor: 'pointer', mb: 3 }} direction="row" alignItems="center">
          <FaArrowLeftLong size={24} />
          <Typography variant="h4">Add Contractor</Typography>
        </Stack>
        <Box component="form" noValidate autoComplete="off">
          <Stack sx={{ width: { xs: '100%', md: '48%' }, mb: 3 }}>
            <FormLabel sx={{ mb: 1 }}>Name <InfoTooltip title="Label for this contractor profile, e.g. the property owner or company the management contract is offered to." /></FormLabel>
            <TextField label="Enter Name" value={name} onChange={(e) => setName(e.target.value)} />
          </Stack>
          <Alert severity="info" sx={{ mb: 3 }}>The Standard template is mandatory. If you leave All Inclusive without a template, owners will only see the Standard option.</Alert>
          <Stack spacing={3} direction="row" useFlexGap flexWrap="wrap">
            {renderModeColumn('Standard', 'Default contract proposal shown to every owner.', standard, setStandardField, standardTemplates, true)}
            {renderModeColumn('All Inclusive', 'Optional alternative; only shown when a template is selected.', allInclusive, setAllInclusiveField, allInclusiveTemplates, false)}
          </Stack>
        </Box>
        <LoadingButton sx={{ width: 100, mt: 4, mb: 4, boxShadow: 'none', backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#1E3A5F', boxShadow: 'none' } }} variant="contained" onClick={handleSave} loading={loading}>Save</LoadingButton>
      </Card>
    </>
  );
};

export default AddContractor;
